import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconGrid, IconSearch, IconPlus } from './Icons';
import { useAuth } from '../contexts/AuthContext';
import { sendLeaveRequestSubmittedEmail } from '../services/emailService';
import { normalizeRole } from '../lib/roles';
import './TeamLeave.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const teamLeaveSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const ROLES = { ADMIN: 'admin', HR: 'hr' };

const LEAVE_TYPE_KEYS = [
  { key: 'annual', label: 'Annual Leave', defaultDays: 28 },
  { key: 'emergency', label: 'Emergency Leave', defaultDays: 5 },
  { key: 'sick', label: 'Sick Leave', defaultDays: 5 },
];

const STORAGE_KEY_LEAVE_DAYS = 'leave_days_config';

function getStoredLeaveDays() {
  try {
    const s = localStorage.getItem(STORAGE_KEY_LEAVE_DAYS);
    if (s) {
      const parsed = JSON.parse(s);
      const out = {};
      LEAVE_TYPE_KEYS.forEach(({ key, defaultDays }) => {
        out[key] = Math.max(0, Number(parsed[key]) ?? defaultDays);
      });
      return out;
    }
  } catch (_) {}
  return LEAVE_TYPE_KEYS.reduce((acc, { key, defaultDays }) => ({ ...acc, [key]: defaultDays }), {});
}

const COLUMNS = [
  { key: 'applicant', label: 'Applicant' },
  { key: 'leaveType', label: 'Leave type' },
  { key: 'startDate', label: 'Start date' },
  { key: 'endDate', label: 'End date' },
  { key: 'status', label: 'Status' },
];

export default function TeamLeave() {
  const { user, profile, refreshProfile } = useAuth();
  const role = normalizeRole(profile?.role);
  const canApprove = role === ROLES.ADMIN || role === ROLES.HR;

  const [tab, setTab] = useState('all'); // 'all' | 'pending' | 'approved'
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applying, setApplying] = useState(false);
  /* eslint-disable-next-line no-unused-vars -- used in Edit leave days modal (JSX) */
  const [showEditDaysModal, setShowEditDaysModal] = useState(false);
  /* eslint-disable-next-line no-unused-vars -- setLeaveDays used in Save handler; leaveDays in Apply form dropdown */
  const [leaveDays, setLeaveDays] = useState(() => getStoredLeaveDays());
  /* eslint-disable-next-line no-unused-vars -- used in Edit leave days modal (JSX) */
  const [editDaysDraft, setEditDaysDraft] = useState(null);
  const [applyForm, setApplyForm] = useState({ leaveType: '', startDate: '', endDate: '', reason: '' });
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loadingLeave, setLoadingLeave] = useState(true);
  const [leaveError, setLeaveError] = useState(null);
  const [reviewingRowId, setReviewingRowId] = useState(null);
  const [reviewError, setReviewError] = useState(null);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      teamLeaveSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchLeaveRequests = useCallback(async () => {
    if (!user?.id) {
      setLeaveRequests([]);
      setLoadingLeave(false);
      return;
    }
    setLoadingLeave(true);
    setLeaveError(null);
    try {
      let query = teamLeaveSupabase
        .from('leave_requests')
        .select('*, profiles!applicant_id(full_name)')
        .order('created_at', { ascending: false });
      if (!canApprove) {
        query = query.eq('applicant_id', user.id);
      }
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([query, timeoutPromise]);
      const { data, error } = res;
      if (error) {
        setLeaveError(error.message);
        setLeaveRequests([]);
      } else {
        setLeaveRequests(data ?? []);
      }
    } catch (e) {
      setLeaveError(e?.message || 'Failed to load');
      setLeaveRequests([]);
    } finally {
      setLoadingLeave(false);
    }
  }, [user?.id, canApprove]);

  const refreshSessionAndFetch = useCallback(async () => {
    setLeaveError(null);
    try {
      await teamLeaveSupabase.auth.getSession();
      await runWakeUp();
      await fetchLeaveRequests();
    } catch (e) {
      setLeaveError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchLeaveRequests]);

  const getLeaveBalance = (key) => {
    if (key === 'annual') return profile?.annual_leave_left;
    if (key === 'emergency') return profile?.emergency_leave_left;
    if (key === 'sick') return profile?.sick_leave_left;
    return undefined;
  };
  const selectedBalance = applyForm.leaveType ? getLeaveBalance(applyForm.leaveType) : null;
  const selectedDaysLeft = selectedBalance !== undefined && selectedBalance !== null ? Math.max(0, Number(selectedBalance)) : 0;
  const maxEndDateForBalance = (startDateStr, daysAllowed) => {
    if (!startDateStr || daysAllowed <= 0) return undefined;
    const d = new Date(startDateStr);
    d.setDate(d.getDate() + daysAllowed - 1);
    return d.toISOString().slice(0, 10);
  };
  const endDateMax = applyForm.startDate && selectedDaysLeft > 0
    ? maxEndDateForBalance(applyForm.startDate, selectedDaysLeft)
    : undefined;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await teamLeaveSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setLeaveError(sessionErr.message);
        setLoadingLeave(false);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await fetchLeaveRequests();
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchLeaveRequests]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    if (showApplyModal && user?.id) refreshProfile?.();
  }, [showApplyModal, user?.id, refreshProfile]);

  useEffect(() => {
    if (!applyForm.endDate || !endDateMax) return;
    if (applyForm.endDate > endDateMax) {
      setApplyForm((f) => ({ ...f, endDate: endDateMax }));
    }
  }, [endDateMax, applyForm.endDate]);

  useEffect(() => {
    if (applyForm.leaveType && selectedDaysLeft <= 0) {
      setApplyForm((f) => ({ ...f, leaveType: '', endDate: '' }));
    }
  }, [applyForm.leaveType, selectedDaysLeft]);

  const [visibleColumns, setVisibleColumns] = useState(
    COLUMNS.reduce(
      (acc, col) => ({
        ...acc,
        [col.key]: true,
      }),
      {},
    ),
  );
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleReview = async (rowId, status) => {
    if (!canApprove || !user?.id || reviewingRowId) return;
    setReviewError(null);
    setReviewingRowId(rowId);
    try {
      await runWakeUp();
      const { error } = await teamLeaveSupabase
        .from('leave_requests')
        .update({
          status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', rowId);
      if (error) {
        setReviewError(error.message);
        return;
      }
      await fetchLeaveRequests();
    } catch (err) {
      setReviewError(err?.message || 'Something went wrong.');
    } finally {
      setReviewingRowId(null);
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    setApplyError(null);
    const { leaveType, startDate, endDate } = applyForm;
    if (!leaveType?.trim()) {
      setApplyError('Please select a leave type.');
      return;
    }
    const balance = getLeaveBalance(leaveType);
    const daysLeft = balance !== undefined && balance !== null ? Math.max(0, Number(balance)) : 0;
    if (daysLeft <= 0) {
      setApplyError('You have no days left for this leave type.');
      return;
    }
    if (!startDate?.trim()) {
      setApplyError('Please select a start date.');
      return;
    }
    if (!endDate?.trim()) {
      setApplyError('Please select an end date.');
      return;
    }
    if (new Date(endDate) < new Date(startDate)) {
      setApplyError('End date must be on or after start date.');
      return;
    }
    const daysRequested = (new Date(endDate) - new Date(startDate)) / (24 * 60 * 60 * 1000) + 1;
    if (daysRequested > daysLeft) {
      setApplyError(`You only have ${daysLeft} day${daysLeft === 1 ? '' : 's'} left for this leave type.`);
      return;
    }
    if (!user?.id) {
      setApplyError('You must be logged in to apply.');
      return;
    }
    setApplying(true);
    try {
      await runWakeUp();
      const timeoutMs = 15000;
      const insertPromise = teamLeaveSupabase.from('leave_requests').insert({
        applicant_id: user.id,
        leave_type_key: leaveType.trim(),
        start_date: startDate,
        end_date: endDate,
        reason: applyForm.reason?.trim() || null,
        status: 'pending',
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Check your connection and try again.')), timeoutMs)
      );
      const { error } = await Promise.race([insertPromise, timeoutPromise]);
      if (error) {
        setApplyError(error.message);
        return;
      }
      const typeLabel = getLeaveTypeLabel(leaveType.trim());
      if (user?.email) {
        sendLeaveRequestSubmittedEmail({
          to: user.email,
          employeeName: profile?.full_name,
          leaveTypeLabel: typeLabel,
          startDate,
          endDate,
          reason: applyForm.reason?.trim() || '',
        }).catch((err) => console.warn('[TeamLeave] confirmation email:', err?.message || err));
      }
      setShowApplyModal(false);
      setApplyForm({ leaveType: '', startDate: '', endDate: '', reason: '' });
      fetchLeaveRequests();
    } catch (err) {
      setApplyError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  const activeColumns = COLUMNS.filter((col) => visibleColumns[col.key] !== false);
  const activeColumnCount = activeColumns.length + 1; // +1 for actions column

  const getLeaveTypeLabel = (key) => LEAVE_TYPE_KEYS.find((t) => t.key === key)?.label ?? key ?? '—';

  const handleExportExcel = () => {
    const header = COLUMNS.map((c) => c.label);
    const rows = filteredRequests.map((row) => [
      row.profiles?.full_name ?? '',
      getLeaveTypeLabel(row.leave_type_key),
      row.start_date ?? '',
      row.end_date ?? '',
      row.status ?? '',
    ]);
    const csvLines = [header, ...rows].map((cols) =>
      cols
        .map((v) => {
          const s = v == null ? '' : String(v);
          return s.includes('"') || s.includes(',') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','),
    );
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leave-requests-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredRequests =
    tab === 'all'
      ? leaveRequests
      : tab === 'pending'
        ? leaveRequests.filter((r) => r.status === 'pending')
        : leaveRequests.filter((r) => r.status === 'approved');

  return (
    <div className="tleave-page">
      <div className="tleave-card">
        <div className="tleave-header-row">
          <h1 className="tleave-title">Leave request</h1>
          <div className="tleave-header-actions">
            {canApprove && (
              <button
                type="button"
                className="tleave-header-btn"
                onClick={() => {
                  setEditDaysDraft({ ...leaveDays });
                  setShowEditDaysModal(true);
                }}
              >
                Edit leave days
              </button>
            )}
            <button
              type="button"
              className="tleave-header-btn tleave-header-btn-primary"
              onClick={() => {
                setApplyError(null);
                setShowApplyModal(true);
              }}
            >
              <IconPlus />
              Apply leave
            </button>
          </div>
        </div>

        <div className="tleave-tabs-row">
          <button
            type="button"
            className={`tleave-tab-btn${tab === 'all' ? ' active' : ''}`}
            onClick={() => setTab('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`tleave-tab-btn${tab === 'pending' ? ' active' : ''}`}
            onClick={() => setTab('pending')}
          >
            Pending
          </button>
          <button
            type="button"
            className={`tleave-tab-btn${tab === 'approved' ? ' active' : ''}`}
            onClick={() => setTab('approved')}
          >
            Approved
          </button>
        </div>

        <div className="tleave-filters-row">
          <div className="tleave-filters-left">
            <div className="tleave-column-toggle-wrap">
              <button
                type="button"
                className="tleave-select-toggle tleave-column-toggle"
                aria-label="Choose visible columns"
                onClick={() => setColumnMenuOpen((open) => !open)}
              >
                <span className="tleave-column-dots">≡</span>
              </button>

              {columnMenuOpen && (
                <div className="tleave-column-menu">
                  {COLUMNS.map((col) => (
                    <button
                      key={col.key}
                      type="button"
                      className={`tleave-column-menu-item${
                        visibleColumns[col.key] !== false ? ' active' : ''
                      }`}
                      onClick={() => toggleColumn(col.key)}
                    >
                      <span>{col.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              type="button"
              className="tleave-layout-toggle"
              aria-label="Layout options"
            >
              <IconGrid />
            </button>

            {tab === 'all' && (
              <div className="tleave-month-picker">
                <button
                  type="button"
                  className="tleave-month-arrow"
                  aria-label="Previous month"
                >
                  «
                </button>
                <span className="tleave-month-label">February 2026</span>
                <button
                  type="button"
                  className="tleave-month-arrow"
                  aria-label="Next month"
                >
                  »
                </button>
              </div>
            )}

          </div>

          <div className="tleave-filters-right">
            <button type="button" className="tleave-utility-btn" onClick={handleExportExcel} disabled={loadingLeave || filteredRequests.length === 0}>
              Excel
            </button>
            <div className="tleave-search">
              <input type="text" placeholder="Search" aria-label="Search leaves" />
              <span className="tleave-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        {reviewError && (
          <p className="salary-form-error salary-form-error-inline" role="alert" style={{ marginBottom: '0.5rem' }}>
            {reviewError}
          </p>
        )}

        <div className="tleave-table-wrapper">
          <table className="tleave-table">
            <thead>
              <tr>
                {activeColumns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {loadingLeave ? (
                <tr>
                  <td className="tleave-empty-cell" colSpan={activeColumnCount}>
                    Loading…
                  </td>
                </tr>
              ) : leaveError ? (
                <tr>
                  <td className="tleave-empty-cell" colSpan={activeColumnCount}>
                    {leaveError}
                  </td>
                </tr>
              ) : filteredRequests.length === 0 ? (
                <tr>
                  <td className="tleave-empty-cell" colSpan={activeColumnCount}>
                    {tab === 'all'
                      ? (canApprove ? 'No leave requests.' : 'Your leave requests will appear here. Use "Apply leave" to submit one.')
                      : tab === 'pending'
                        ? 'No pending requests.'
                        : 'No approved requests.'}
                  </td>
                </tr>
              ) : (
                filteredRequests.map((row) => (
                  <tr key={row.id}>
                    {activeColumns.map((col) => {
                      if (col.key === 'applicant') {
                        return (
                          <td key={col.key}>
                            <div className="tleave-applicant-cell">
                              <div className="tleave-avatar" />
                              <span className="tleave-applicant-link">
                                {row.profiles?.full_name ?? '—'}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      if (col.key === 'leaveType') {
                        return (
                          <td key={col.key}>
                            <span className="tleave-leave-color" />
                            {getLeaveTypeLabel(row.leave_type_key)}
                          </td>
                        );
                      }
                      if (col.key === 'startDate') {
                        return <td key={col.key}>{row.start_date ?? '—'}</td>;
                      }
                      if (col.key === 'endDate') {
                        return <td key={col.key}>{row.end_date ?? '—'}</td>;
                      }
                      if (col.key === 'status') {
                        return (
                          <td key={col.key}>
                            <span className="tleave-status-pill">{row.status ?? '—'}</span>
                          </td>
                        );
                      }
                      return null;
                    })}
                    <td>
                      {canApprove && row.status === 'pending' && (
                        <div className="tleave-row-actions">
                          <button
                            type="button"
                            className="tleave-row-icon-btn"
                            aria-label="Approve"
                            title="Approve"
                            disabled={reviewingRowId === row.id}
                            onClick={() => handleReview(row.id, 'approved')}
                          >
                            {reviewingRowId === row.id ? '…' : '✓'}
                          </button>
                          <button
                            type="button"
                            className="tleave-row-icon-btn"
                            aria-label="Reject"
                            title="Reject"
                            disabled={reviewingRowId === row.id}
                            onClick={() => handleReview(row.id, 'rejected')}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="tleave-footer-row">
          <div className="tleave-footer-left">
            <select
              className="tleave-page-size"
              defaultValue="10"
              aria-label="Rows per page"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>0-0 / 0</span>
          </div>
          <div className="tleave-footer-right">
            <button
              type="button"
              className="tleave-page-btn"
              aria-label="Previous page"
            >
              «
            </button>
            <button
              type="button"
              className="tleave-page-btn tleave-page-btn-active"
              aria-label="Current page"
            >
              1
            </button>
            <button
              type="button"
              className="tleave-page-btn"
              aria-label="Next page"
            >
              »
            </button>
          </div>
        </div>
      </div>

      {showApplyModal && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => {
            setShowApplyModal(false);
            setApplyForm({ leaveType: '', startDate: '', endDate: '', reason: '' });
          }}
        >
          <div
            className="estimates-modal"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Apply leave</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => {
                  setShowApplyModal(false);
                  setApplyForm({ leaveType: '', startDate: '', endDate: '', reason: '' });
                  setApplyError(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleApplySubmit}>
            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                <div className="estimates-form-label">Leave type</div>
                <select
                  className="estimates-form-select"
                  value={applyForm.leaveType}
                  onChange={(e) => setApplyForm((f) => ({ ...f, leaveType: e.target.value }))}
                >
                  <option value="">- Select -</option>
                  {LEAVE_TYPE_KEYS.map(({ key, label }) => {
                    const balance = getLeaveBalance(key);
                    const days = balance !== undefined && balance !== null ? Math.max(0, Number(balance)) : (leaveDays[key] ?? 0);
                    const noDaysLeft = days <= 0;
                    return (
                      <option key={key} value={key} disabled={noDaysLeft}>
                        {label} ({days} days left){noDaysLeft ? ' — unavailable' : ''}
                      </option>
                    );
                  })}
                </select>

                <div className="estimates-form-label">Start date</div>
                <input
                  type="date"
                  className="estimates-form-input"
                  value={applyForm.startDate}
                  onChange={(e) => setApplyForm((f) => ({ ...f, startDate: e.target.value }))}
                  aria-label="Start date"
                />

                <div className="estimates-form-label">End date</div>
                <input
                  type="date"
                  className="estimates-form-input"
                  value={applyForm.endDate}
                  onChange={(e) => setApplyForm((f) => ({ ...f, endDate: e.target.value }))}
                  aria-label="End date"
                  min={applyForm.startDate || undefined}
                  max={endDateMax}
                />

                <div className="estimates-form-label">Reason</div>
                <textarea
                  className="estimates-form-textarea"
                  placeholder="Reason"
                  value={applyForm.reason}
                  onChange={(e) => setApplyForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </div>
            </div>

              {applyError && (
                <p className="salary-form-error salary-form-error-inline" role="alert">
                  {applyError}
                </p>
              )}
            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => {
                  setShowApplyModal(false);
                  setApplyForm({ leaveType: '', startDate: '', endDate: '', reason: '' });
                  setApplyError(null);
                }}
              >
                Close
              </button>
              <button
                type="submit"
                className="estimates-footer-btn estimates-footer-btn-primary"
                disabled={applying || selectedDaysLeft <= 0}
              >
                {applying ? 'Applying…' : 'Apply leave'}
              </button>
            </div>
            </form>
          </div>
        </div>
      )}

      {showEditDaysModal && editDaysDraft && (
        <div
          className="estimates-modal-backdrop"
          onClick={() => setShowEditDaysModal(false)}
        >
          <div
            className="estimates-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Edit leave days</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowEditDaysModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="estimates-modal-body">
              <div className="estimates-form-grid">
                {LEAVE_TYPE_KEYS.map(({ key, label }) => (
                  <React.Fragment key={key}>
                    <div className="estimates-form-label">{label}</div>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className="estimates-form-input"
                      value={editDaysDraft[key] ?? 0}
                      onChange={(e) =>
                        setEditDaysDraft((d) => ({
                          ...d,
                          [key]: Math.max(0, parseInt(e.target.value, 10) || 0),
                        }))
                      }
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => setShowEditDaysModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={() => {
                  setLeaveDays(editDaysDraft);
                  try {
                    localStorage.setItem(STORAGE_KEY_LEAVE_DAYS, JSON.stringify(editDaysDraft));
                  } catch (_) {}
                  setShowEditDaysModal(false);
                  setEditDaysDraft(null);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

